import type { CharacterStatus, CharacterVisibility, DynamicCharacterDefinition } from './character-catalog'
import type {
  GeminiAspectRatio,
  GeminiEnrichmentModel,
  GeminiImageModel,
  GeminiImageSize,
  GeminiThinkingLevel
} from './constants/gemini-image-model-manifest'
import type { EnrichmentPromptTemplateSettings } from './enrichment-prompts'
import type { PublicFailure } from './errors'
import type { GenerationCreator } from './generation-creator'
import type { ReferenceAssetSnapshot, ReferenceBuilderInput } from './reference-assets'
import type { GenerationWarning } from './scale-references'

export type {
  GeminiAspectRatio,
  GeminiEnrichmentModel,
  GeminiImageModel,
  GeminiImageSize,
  GeminiThinkingLevel
} from './constants/gemini-image-model-manifest'

export type JobStatus = 'pending' | 'processing' | 'done' | 'error' | 'moderation_failed'
// Single list of Playground tool types. Iterate this (cost tracking, history
// fan-out) instead of re-listing tools, so a new tool can't be silently omitted.
export const PLAYGROUND_TOOL_TYPES = [
  'enrichment',
  // Retained so history and cost tracking still report pre-EC2-retirement records.
  'character-wan',
  'background',
  'inpaint',
  'character-nanobanana',
  'remove-background',
  'composition',
  'outpaint',
  'upscale',
  'freeflow',
  'reference-builder'
] as const
export type PlaygroundToolType = (typeof PLAYGROUND_TOOL_TYPES)[number]

// ============================================================================
// WORKFLOW V2 TYPES - Immutable multi-step generation workspaces
// ============================================================================

/**
 * The guided workflow has a stable parent workspace and immutable generation
 * runs beneath it. `scene` is the direct "place character in scene" branch;
 * `background` + `composition` is the separated branch.
 */
export const WORKFLOW_V2_STAGES = [
  'character',
  'scene',
  'background',
  'composition',
  'inpaint',
  'outpaint',
  'upscale'
] as const
export type WorkflowV2Stage = (typeof WORKFLOW_V2_STAGES)[number]

export type WorkflowSceneStrategy = 'combined-scene' | 'separate-background'
/**
 * `awaiting-selection` is retained only so pre-rollout rows remain readable.
 * New workflows settle as `completed`; selection is an editor interaction, not
 * a workflow state that belongs in history.
 */
export type WorkflowV2Status = 'active' | 'awaiting-selection' | 'completed' | 'error' | 'archived'
/** Engine recorded for a legacy workflow imported into Workflow V2. */
export type LegacyWorkflowGenerationType = 'ec2' | 'nanobanana' | 'unknown'
/** Original stage retained solely as read-only provenance for imported assets. */
export type LegacyWorkflowStep = 'character' | 'background' | 'inpaint'

export interface Workflow {
  workflowId: string
  schemaVersion: 2
  /** Shared index partition for durable V2 workspace history. */
  workspaceId: 'shared'
  status: WorkflowV2Status
  character: Character
  folderId?: string
  sceneStrategy?: WorkflowSceneStrategy
  /** Present only for an imported legacy workflow. */
  legacyJobId?: string
  /**
   * Retains whether a legacy workflow came from EC2 or Nano Banana. Missing
   * values on old imported rows are treated as
   * unknown and therefore conservatively read-only until backfilled.
   */
  legacyGenerationType?: LegacyWorkflowGenerationType
  createdAt: string
  updatedAt: string
}

/** EC2 and unclassified legacy imports are downloadable archives, never editable workspaces. */
export function isReadOnlyLegacyWorkflow(workflow: Pick<Workflow, 'legacyJobId' | 'legacyGenerationType'>): boolean {
  return !!workflow.legacyJobId && workflow.legacyGenerationType !== 'nanobanana'
}

/** One immutable provider submission within a Workflow V2 workspace. */
export interface WorkflowRun extends ReferenceAssetSnapshot {
  createdBy?: GenerationCreator
  runId: string
  workflowId: string
  /**
   * Candidates created from one user action share a batch. Older V2 rows did
   * not persist this field; consumers use the run id as their implicit batch.
   */
  batchId?: string
  /** Stable display/order position inside a candidate batch. */
  batchIndex?: number
  stage: WorkflowV2Stage
  /** Reuses the existing Gemini processor capability for this stage. */
  toolType: Exclude<
    PlaygroundToolType,
    'enrichment' | 'character-wan' | 'remove-background' | 'freeflow' | 'reference-builder'
  >
  status: PlaygroundRunStatus
  requestedAssetCount: 1 | 2 | 3 | 4
  completedAssetCount: number
  failedAssetCount: number
  assetIds: string[]
  /** Exact image lineage used by this submission; never inferred from "latest". */
  sourceAssetIds: string[]
  retryOfRunId?: string
  /** The failed batch this run retries. Every run in a retry batch carries the same value. */
  retryOfBatchId?: string
  /**
   * Generation deadline. The stale-run reconciler fails a run still pending or
   * processing after it, so the editor can offer Retry instead of waiting forever.
   * Removed once the run settles, which also drops it from the sparse expiry index.
   */
  expiresAt?: string
  character?: Character
  userInput: string
  /** Whether this run used its supplied prompt verbatim instead of enrichment. */
  skipEnrichment?: boolean
  enrichedPrompt?: string
  referenceImageKeys?: string[]
  maskImageKey?: string
  outpaintLayoutGuideImageKey?: string
  outpaint?: OutpaintInput
  upscale?: UpscaleInput
  geminiImageSettings: GeminiImageGenerationSettings
  tokensUsed?: TokenUsageEntry[]
  geminiCallCount?: number
  totalTokens?: number
  estimatedCostUsd?: number
  failure?: PublicFailure
  errorMessage?: string
  createdAt: string
  updatedAt: string
}

/**
 * Returns the editable user instruction for a saved workflow generation.
 * Upscale stores its instruction in its tool-specific input because it does
 * not submit a free-form top-level prompt.
 */
export function getWorkflowRunPrompt(run: WorkflowRun): string {
  if (run.stage === 'upscale') return run.upscale?.userPrompt ?? run.userInput
  if (run.stage === 'outpaint') return run.outpaint?.userPrompt ?? run.userInput
  return run.userInput
}

/** A single durable image candidate generated by a Workflow V2 run. */
export interface WorkflowAsset {
  createdBy?: GenerationCreator
  generationWarnings?: GenerationWarning[]
  assetId: string
  workflowId: string
  runId: string
  /** Mirrors its run's batch for efficient UI grouping. */
  batchId?: string
  /** Exact immutable parent images used to create this candidate. */
  parentAssetIds?: string[]
  stage: WorkflowV2Stage
  toolType: WorkflowRun['toolType']
  status: JobStatus
  variantIndex: number
  outputImageKey?: string
  failure?: PublicFailure
  errorMessage?: string
  duration?: number
  tokensUsed?: TokenUsageEntry[]
  geminiCallCount?: number
  totalTokens?: number
  estimatedCostUsd?: number
  /**
   * Provenance is kept even when an image was not generated by this workflow.
   * It lets workflow cleanup distinguish generated outputs from reusable
   * user-uploaded inputs from generated workflow output.
   */
  source?: { kind: 'legacy-job'; jobId: string; step: LegacyWorkflowStep } | { kind: 'user-upload'; imageKey: string }
  createdAt: string
  updatedAt: string
}

/** A small, display-safe image record for the Workflow History index. */
export interface WorkflowHistoryPreviewAsset {
  assetId: string
  stage: WorkflowV2Stage
  outputImageKey: string
  createdAt: string
  updatedAt: string
}

/**
 * History intentionally returns a capped preview rather than every asset in a
 * workspace. The full batch and lineage graph remains available on the
 * workflow and asset-gallery routes.
 */
export interface WorkflowHistoryItem extends Workflow {
  assetCount: number
  completedAssetCount: number
  finishingAssetCount: number
  previewAssets: WorkflowHistoryPreviewAsset[]
}

const isFinishingAsset = (asset: WorkflowAsset) =>
  asset.stage === 'inpaint' || asset.stage === 'outpaint' || asset.stage === 'upscale'

/** Builds the bounded history-card summary from immutable workspace assets. */
export function buildWorkflowHistoryItem(workflow: Workflow, assets: WorkflowAsset[]): WorkflowHistoryItem {
  const completedAssets = assets.filter((asset) => asset.status === 'done' && !!asset.outputImageKey)
  const newestFirst = (left: WorkflowAsset, right: WorkflowAsset) =>
    right.updatedAt.localeCompare(left.updatedAt) ||
    right.createdAt.localeCompare(left.createdAt) ||
    right.assetId.localeCompare(left.assetId)
  const recentAssets = [...completedAssets].sort(newestFirst)
  const characterAsset = recentAssets.find((asset) => asset.stage === 'character')
  const previewAssets = [
    ...(characterAsset ? [characterAsset] : []),
    ...recentAssets.filter((asset) => asset.assetId !== characterAsset?.assetId)
  ]
    .slice(0, 4)
    .map((asset) => ({
      assetId: asset.assetId,
      stage: asset.stage,
      outputImageKey: asset.outputImageKey as string,
      createdAt: asset.createdAt,
      updatedAt: asset.updatedAt
    }))

  return {
    ...workflow,
    // Older deployed rows used this as a proxy for a successful core batch.
    // History has only Processing, Done, and Error, so normalize it at the
    // API boundary without rewriting a user's existing record.
    status: workflow.status === 'awaiting-selection' ? 'completed' : workflow.status,
    assetCount: assets.length,
    completedAssetCount: completedAssets.length,
    finishingAssetCount: completedAssets.filter(isFinishingAsset).length,
    previewAssets
  }
}

/**
 * A user-visible set of candidates. It is derived from immutable runs rather
 * than stored separately, so deploying fan-out does not require a new table
 * or rewriting any existing Workflow V2 rows.
 */
export interface WorkflowBatch {
  batchId: string
  workflowId: string
  stage: WorkflowV2Stage
  toolType: WorkflowRun['toolType']
  status: PlaygroundRunStatus
  requestedCandidateCount: number
  completedCandidateCount: number
  failedCandidateCount: number
  runIds: string[]
  assetIds: string[]
  sourceAssetIds: string[]
  /** The failed batch this one retries. */
  retryOfBatchId?: string
  /** The newest batch that retries this one; its failures need no further retry. */
  retriedByBatchId?: string
  createdAt: string
  updatedAt: string
}

/** Returns the persisted batch id, or an implicit one for pre-batch V2 rows. */
export const getWorkflowBatchId = (run: WorkflowRun): string => run.batchId ?? run.runId

/**
 * Groups both new fan-out records and the original one-run/many-asset records
 * into the same UI/API shape. Each candidate is counted from an asset row so
 * partial batches retain their successful images.
 */
export function buildWorkflowBatches(runs: WorkflowRun[], assets: WorkflowAsset[]): WorkflowBatch[] {
  const runsById = new Map(runs.map((run) => [run.runId, run]))
  const batches = new Map<string, WorkflowRun[]>()
  for (const run of runs) {
    const batchId = getWorkflowBatchId(run)
    const group = batches.get(batchId) ?? []
    group.push(run)
    batches.set(batchId, group)
  }

  const workflowBatches = [...batches.entries()]
    .map(([batchId, batchRuns]): WorkflowBatch => {
      const first = batchRuns[0]
      if (!first) throw new Error(`Workflow batch ${batchId} has no runs.`)
      const batchAssets = assets
        .filter((asset) => {
          const run = runsById.get(asset.runId)
          return (asset.batchId ?? (run ? getWorkflowBatchId(run) : asset.runId)) === batchId
        })
        .sort((left, right) => left.variantIndex - right.variantIndex)
      const requestedCandidateCount = Math.max(
        batchAssets.length,
        batchRuns.reduce((count, run) => count + run.requestedAssetCount, 0)
      )
      const completedCandidateCount = batchAssets.filter((asset) => asset.status === 'done').length
      const failedCandidateCount = batchAssets.filter((asset) => asset.status === 'error').length
      const retryOfBatchId = batchRuns.find((run) => run.retryOfBatchId)?.retryOfBatchId
      const isActive = batchRuns.some((run) => run.status === 'pending' || run.status === 'processing')
      const status: PlaygroundRunStatus = isActive
        ? batchRuns.some((run) => run.status === 'processing')
          ? 'processing'
          : 'pending'
        : completedCandidateCount === 0
          ? 'error'
          : failedCandidateCount > 0
            ? 'partial'
            : 'done'

      return {
        batchId,
        workflowId: first.workflowId,
        stage: first.stage,
        toolType: first.toolType,
        status,
        requestedCandidateCount,
        completedCandidateCount,
        failedCandidateCount,
        runIds: batchRuns.map((run) => run.runId),
        assetIds: batchAssets.map((asset) => asset.assetId),
        sourceAssetIds: [...new Set(batchRuns.flatMap((run) => run.sourceAssetIds))],
        ...(retryOfBatchId && { retryOfBatchId }),
        createdAt: batchRuns.reduce(
          (earliest, run) => (run.createdAt < earliest ? run.createdAt : earliest),
          first.createdAt
        ),
        updatedAt: batchRuns.reduce((latest, run) => (run.updatedAt > latest ? run.updatedAt : latest), first.updatedAt)
      }
    })
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt))

  // Batches are oldest first, so the last retry seen for a batch is its newest.
  const batchesById = new Map(workflowBatches.map((batch) => [batch.batchId, batch]))
  workflowBatches.forEach((batch) => {
    const original = batch.retryOfBatchId ? batchesById.get(batch.retryOfBatchId) : undefined
    if (original) original.retriedByBatchId = batch.batchId
  })

  return workflowBatches
}

export interface WorkflowWorkspaceResponse {
  workflow: Workflow
  runs: WorkflowRun[]
  assets: WorkflowAsset[]
  batches: WorkflowBatch[]
}
/** Code-defined characters from the shared registry. They seed every stage. */
export type PrincipalCharacter =
  | 'doughboy'
  | 'doughboy-2'
  | 'doughboy-3'
  | 'buzz'
  | 'bowie'
  | 'buzzhands'
  | 'cinnamon-toast-crunch'
  | 'cinnamon-toast-crunch-group'
/**
 * Any character ID: a principal character or a dynamic character created on the
 * Character Onboarding page. The branded string keeps editor autocomplete for principals.
 */
export type Character = PrincipalCharacter | (string & NonNullable<unknown>)
export type GeminiImageSettingsSource = 'override' | 'character' | 'code'

export interface WorkflowFolder {
  folderId: string
  name: string
  normalizedName: string
  createdAt: string
  updatedAt: string
  workflowCount: number
  lastActivityAt?: string
}

export interface GeminiImageSettings {
  model: GeminiImageModel
  temperature: number
  aspectRatio: GeminiAspectRatio
  imageSize: GeminiImageSize
  thinkingLevel: GeminiThinkingLevel
  /** Enables web grounding. Omitted historical settings keep Google Search off. */
  googleSearch?: boolean
}

/**
 * Settings captured for an individual generation. A null aspect ratio leaves
 * Gemini's aspectRatio field unset so it can use its reference/default behavior.
 */
export interface GeminiImageGenerationSettings extends Omit<GeminiImageSettings, 'aspectRatio'> {
  aspectRatio: GeminiAspectRatio | null
}

export interface GeminiEnrichmentSettings {
  model: GeminiEnrichmentModel
}

export interface CharacterGeminiImageSettings extends EnrichmentPromptTemplateSettings {
  characterSheetImageKey?: string
  scaleReferenceBriefs?: Record<string, string>
  /** Absent inherits the legacy default; [] explicitly disables scale references. */
  scaleReferenceImageKeys?: string[]
  /** Pending appearance uploads, keyed by object key with expiry in epoch milliseconds. */
  referenceUploadReservations?: Record<string, number>
  character: Character
  /** Present only for dynamic characters created through Character Onboarding. */
  definition?: DynamicCharacterDefinition
  status?: CharacterStatus
  /** Any character, principal or dynamic. Absent means `all`. */
  visibility?: CharacterVisibility
  settings?: GeminiImageSettings
  enrichmentSettings?: GeminiEnrichmentSettings
  guidelinesUrl?: string
  /** Set only on the tombstone left by deleting a dynamic character; it reserves the ID. */
  deletedAt?: string
  deletedDisplayName?: string
  createdAt: string
  updatedAt: string
}

// Upload purpose types for presigned URL uploads
// Used by both frontend and backend to ensure type safety
export const UPLOAD_PURPOSES = [
  'background',
  'inpaint',
  'character',
  'reference',
  'remove-background',
  'composition',
  'outpaint',
  'upscale'
] as const
export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number]

/** Image types accepted by the presigned upload endpoints. */
export const UPLOAD_IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'] as const

const MB = 1024 * 1024

// Default matches the Gemini (Nano Banana Pro) source-image limit.
export const DEFAULT_UPLOAD_MAX_BYTES = 7 * MB

/** Per-purpose overrides of DEFAULT_UPLOAD_MAX_BYTES, enforced by the backend and pre-checked by the frontend. */
export const UPLOAD_MAX_BYTES_BY_PURPOSE: Readonly<Partial<Record<UploadPurpose, number>>> = {
  reference: 2 * MB,
  // A lossless 4K layout guide can exceed the normal source-image limit.
  outpaint: 25 * MB,
  upscale: 25 * MB
}

export function getUploadMaxBytes(purpose: UploadPurpose): number {
  return UPLOAD_MAX_BYTES_BY_PURPOSE[purpose] ?? DEFAULT_UPLOAD_MAX_BYTES
}

/** Admin-managed character reference images (utility bucket). */
export const ADMIN_REFERENCE_IMAGE_MAX_BYTES = 3 * MB
export const ADMIN_REFERENCE_IMAGE_MAX_COUNT = 6

// ============================================================================
// TOKEN USAGE - Recorded every time a Gemini API call is made
// ============================================================================

// Which kind of Gemini call produced this usage entry
// - enrich-text: text-only enrichment call (enhance-prompt lambda)
// - enrich-pose: pose extraction vision call (enhance-prompt lambda, nanobanana flow)
// - character / background / inpaint / remove-background / composition / freeflow: image generation call
export type TokenUsageType =
  | 'enrich-text'
  | 'enrich-pose'
  | 'character'
  | 'background'
  | 'inpaint'
  | 'remove-background'
  | 'composition'
  | 'outpaint'
  | 'upscale'
  | 'freeflow'

export interface TokenUsageEntry {
  type: TokenUsageType
  model: string
  imageSize?: GeminiImageSize
  inputTokens: number
  // A subset of outputTokens. Kept separately because image output has a
  // dedicated price, while model reasoning is billed at the text-output rate.
  // Optional so existing persisted usage rows remain readable.
  thinkingTokens?: number
  /** Unique, nonempty Google Search queries reported by Gemini; absent on historical usage. */
  googleSearchQueryCount?: number
  outputTokens: number
  totalTokens: number
  at: string // ISO timestamp
}

// ============================================================================
// PLAYGROUND TYPES - Simple experiments for testing individual tools
// ============================================================================

export interface OutpaintPlacement {
  x: number
  y: number
  width: number
  height: number
}

export interface OutpaintInput {
  mode: 'guided' | 'prompt-only'
  /** Omitted historical requests use the Outpaint model default. */
  model?: GeminiImageModel
  aspectRatio: GeminiAspectRatio
  imageSize: GeminiImageSize
  outputWidth: number
  outputHeight: number
  placement?: OutpaintPlacement
  /** Whether the guided layout softens the source perimeter before export. Missing historical values default to true. */
  featherSourceEdge?: boolean
  /** The raw optional text entered by the user; the provider prompt is derived server-side. */
  userPrompt?: string
  /**
   * Playground Gemini sampling temperature. Missing historical values default to
   * DEFAULT_GEMINI_IMAGE_SETTINGS.temperature. Workflow sends temperature in geminiImageSettings instead.
   */
  temperature?: number
}

/** Immutable AI Upscale configuration, including the editable Gemini instruction. */
export interface UpscaleInput {
  mode: 'preserve'
  /** Omitted historical requests use the AI Upscale model default. */
  model?: GeminiImageModel
  aspectRatio: GeminiAspectRatio
  imageSize: GeminiImageSize
  sourceWidth: number
  sourceHeight: number
  sourceFileName?: string
  sourceFileSize?: number
  /** The raw instruction sent to Gemini; older runs fall back to the shared preserve default. */
  userPrompt?: string
  /**
   * Playground Gemini sampling temperature. Missing historical values default to
   * DEFAULT_GEMINI_IMAGE_SETTINGS.temperature. Workflow sends temperature in geminiImageSettings instead.
   */
  temperature?: number
  promptVersion: 'upscale-preserve-v1'
}

export interface PlaygroundJob extends ReferenceAssetSnapshot {
  createdBy?: GenerationCreator
  generationWarnings?: GenerationWarning[]
  /** Internal Workflow adapter flag: scale images must not insert a foreground subject. */
  backgroundOnly?: boolean
  jobId: string
  status: JobStatus
  createdAt: string
  updatedAt: string

  referenceBuilder?: ReferenceBuilderInput

  // Tool type being tested
  toolType: PlaygroundToolType

  // Present only for immutable Playground run assets. Rows without these
  // fields are legacy single-generation history entries.
  runId?: string
  experimentId?: string
  variantIndex?: number

  // Character (for nanobanana tool type)
  character?: Character
  geminiImageSettings?: GeminiImageGenerationSettings

  // User input (raw prompt from user)
  userInput: string

  // Enriched prompt (from Gemini, if applicable)
  enrichedPrompt?: string

  // Input images (for tools that require them)
  inputImageKey?: string // Primary input image (e.g., character for background gen or composition)
  maskImageKey?: string // Mask for inpainting
  backgroundImageKey?: string // Background scene image (composition tool)
  referenceImageKeys?: string[] // User-uploaded reference images (pose, vibe, etc.)
  /** Resolved output key for a completed FreeFlow variation used as the continuation frame. */
  startingFrameImageKey?: string
  /** Immutable job provenance for `startingFrameImageKey`. */
  startingFrameJobId?: string
  outpaintLayoutGuideImageKey?: string // Layout guide for guided Outpaint
  outpaint?: OutpaintInput
  upscale?: UpscaleInput

  // Output
  outputImageKey?: string // S3 key to the generated result
  failure?: PublicFailure
  errorMessage?: string

  // Processing metadata
  duration?: number // Time in milliseconds

  // Token / retry tracking (one entry per Gemini API call made for this row)
  retryCount?: number
  tokensUsed?: TokenUsageEntry[]
  geminiCallCount?: number
  totalTokens?: number
  estimatedCostUsd?: number
  usageAggregatesVersion?: number
}

export interface CreatePlaygroundJobInput extends ReferenceAssetSnapshot {
  createdBy?: GenerationCreator
  referenceBuilder?: ReferenceBuilderInput
  jobId: string
  toolType: PlaygroundToolType
  userInput: string
  enrichedPrompt?: string
  inputImageKey?: string
  maskImageKey?: string
  backgroundImageKey?: string
  outpaintLayoutGuideImageKey?: string
  outpaint?: OutpaintInput
  upscale?: UpscaleInput
  character?: Character
  referenceImageKeys?: string[]
  startingFrameImageKey?: string
  startingFrameJobId?: string
  geminiImageSettings?: GeminiImageGenerationSettings
  runId?: string
  experimentId?: string
  variantIndex?: number
}

export interface UpdatePlaygroundJobInput {
  generationWarnings?: GenerationWarning[]
  status?: JobStatus
  enrichedPrompt?: string
  outputImageKey?: string
  errorMessage?: string
  duration?: number
  geminiImageSettings?: GeminiImageGenerationSettings

  // Atomic append-only token tracking. Values are added via DynamoDB
  // list_append (tokensUsed) and ADD (retryCount), not overwritten.
  appendTokens?: TokenUsageEntry[]
  incrementRetry?: number
}

export type PlaygroundRunStatus = 'pending' | 'processing' | 'done' | 'partial' | 'error'

/**
 * A Playground run is an immutable submission with one or more image assets.
 * Prompt enrichment is intentionally excluded: it creates text, not an
 * image asset, so it does not belong in the asset/run history model.
 */
export type PlaygroundImageRunToolType = Exclude<PlaygroundToolType, 'enrichment'>

export interface GeminiDebugImagePart {
  kind: 'reference-image'
  sourceKey: string
  mimeType: string
  sha256?: string
  bytes?: number
  base64: '[redacted]'
}

/**
 * The final Gemini SDK request shape, with binary input deliberately removed.
 * It is stored only for the Nano Banana Playground pilot's local debug view.
 */
export interface GeminiDebugRequest {
  model: GeminiImageModel
  config: Record<string, unknown>
  contents: {
    role: 'user'
    parts: Array<{ text: string } | { inlineData: GeminiDebugImagePart }>
  }[]
  capturedAt: string
}

export interface PlaygroundRun {
  createdBy?: GenerationCreator
  runId: string
  experimentId: string
  parentRunId?: string
  workspaceId: 'shared'
  toolType: PlaygroundImageRunToolType
  status: PlaygroundRunStatus
  requestedAssetCount: 1 | 2 | 3 | 4
  completedAssetCount: number
  failedAssetCount: number
  assetJobIds: string[]
  character?: Character
  userInput: string
  enrichedPrompt?: string
  /** Immutable source material used to make the run reproducible. */
  inputSnapshot?: Pick<
    PlaygroundJob,
    | 'inputImageKey'
    | 'maskImageKey'
    | 'backgroundImageKey'
    | 'referenceImageKeys'
    | 'useAppearanceReferences'
    | 'useCharacterSheet'
    | 'appearanceReferenceImageKeys'
    | 'characterSheetImageKey'
    | 'scaleReferenceBriefs'
    | 'referenceBuilder'
    | 'useScaleReferences'
    | 'scaleReferenceImageKeys'
    | 'startingFrameImageKey'
    | 'startingFrameJobId'
    | 'outpaintLayoutGuideImageKey'
    | 'outpaint'
    | 'upscale'
  >
  referenceImageKeys?: string[]
  geminiImageSettings: GeminiImageGenerationSettings
  tokensUsed?: TokenUsageEntry[]
  geminiCallCount?: number
  totalTokens?: number
  estimatedCostUsd?: number
  debugRequest?: GeminiDebugRequest
  /** Present only on archival parent records made from pre-run history rows. */
  importVersion?: string
  /** The original Playground asset row retained beneath an archival run. */
  sourceLegacyJobId?: string
  /** Backend deadline after which unresolved assets are finalized as failed. */
  expiresAt?: string
  createdAt: string
  updatedAt: string
}

// ============================================================================
// COST TRACKING - GET /admin/cost-tracking/{character} response
// ============================================================================

export type CostTrackingBucket = {
  geminiCalls: number
  tokens: number
  estimatedCostUsd: number
}

export type CostTrackingBreakdownBucket = CostTrackingBucket & {
  generations: number
}

export type CostTrackingWorkflowMetrics = {
  totalWorkflows: number
  byStage: Record<WorkflowV2Stage, CostTrackingBreakdownBucket>
  totals: CostTrackingBucket
}

export type CostTrackingPlaygroundMetrics = {
  totalJobs: number
  byTool: Record<PlaygroundToolType, CostTrackingBreakdownBucket>
  totals: CostTrackingBucket
}

export type CostTrackingMetrics = {
  character: Character
  selected: {
    workflow: CostTrackingWorkflowMetrics
    playground: CostTrackingPlaygroundMetrics
  }
  globalPlayground: CostTrackingPlaygroundMetrics
  allStageGemini: {
    workflow: CostTrackingBucket
    attributedPlayground: CostTrackingBucket
    globalPlayground: CostTrackingBucket
    estimatedCostUsd: number
  }
}
